import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { configService } from '../services/configService';
import { registrationService } from '../services/registrationService';

const { FiArrowRight, FiClock, FiTrendingUp } = FiIcons;
const TOTAL_SEATS = 39;

const getValidDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTierState = (config, now) => {
  const tiers = [1, 2, 3, 4, 5].map((number) => ({
    id: number,
    name:
      number === 1
        ? 'Tier 1: Early Bird'
        : `Tier ${number}: รอบ${number === 2 ? 'แรก' : number === 3 ? 'สอง' : number === 4 ? 'สาม' : 'ราคาเต็ม'}`,
    price: config[`tier${number}_price`],
    date: getValidDate(config[`tier${number}_date`]),
  }));

  const activeIndex = tiers.findIndex((tier) => tier.date && now < tier.date);

  return {
    tiers,
    activeIndex: activeIndex === -1 ? tiers.length - 1 : activeIndex,
  };
};

const TierCountdown = ({ targetDate, now }) => {
  const difference = targetDate.getTime() - now.getTime();

  if (difference <= 0) {
    return null;
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((difference / 1000 / 60) % 60);
  const seconds = Math.floor((difference / 1000) % 60);

  return (
    <div className="flex items-center gap-2 mt-3">
      <div className="text-[10px] font-black uppercase tracking-tighter text-brand-red animate-pulse">
        จะปรับราคาในอีก:
      </div>
      <div className="flex gap-1.5">
        {[
          { value: days, label: 'วัน' },
          { value: hours, label: 'ชม.' },
          { value: minutes, label: 'นาที' },
          { value: seconds, label: 's' },
        ].map((item) => (
          <div
            key={item.label}
            className="flex items-baseline gap-0.5 bg-zinc-900 text-white px-2 py-0.5 rounded text-[10px] font-black border border-zinc-700"
          >
            <span className="text-sm">{item.value.toString().padStart(2, '0')}</span>
            <span className="text-[8px] opacity-60 font-bold">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function PricingLadder() {
  const [config, setConfig] = useState(null);
  const [remainingSeats, setRemainingSeats] = useState(TOTAL_SEATS);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settings, registrations] = await Promise.all([
          configService.getAll(),
          registrationService.getAll(),
        ]);

        setConfig(settings);
        setRemainingSeats(
          Math.max(0, TOTAL_SEATS - registrations.length)
        );
      } catch (error) {
        console.error('Failed to load pricing data:', error);
      }
    };

    loadData();

    const syncTimer = setInterval(loadData, 60000);
    const clockTimer = setInterval(() => setNow(new Date()), 1000);

    return () => {
      clearInterval(syncTimer);
      clearInterval(clockTimer);
    };
  }, []);

  const { tiers, activeIndex } = useMemo(() => {
    if (!config) {
      return {
        tiers: [],
        activeIndex: 0,
      };
    }

    return getTierState(config, now);
  }, [config, now]);

  const formatThaiDate = (date) => {
    if (!date) {
      return '-';
    }

    const months = [
      'ม.ค.',
      'ก.พ.',
      'มี.ค.',
      'เม.ย.',
      'พ.ค.',
      'มิ.ย.',
      'ก.ค.',
      'ส.ค.',
      'ก.ย.',
      'ต.ค.',
      'พ.ย.',
      'ธ.ค.',
    ];

    return `${date.getDate()} ${months[date.getMonth()]} ${String(
      date.getFullYear() + 543
    ).slice(-2)}`;
  };

  if (!config || tiers.length === 0) {
    return null;
  }

  return (
    <section
      id="pricing-ladder"
      className="section-padding bg-zinc-950 text-white relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto relative z-10 px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 bg-brand-red/10 border border-brand-red px-4 py-2 mb-8">
            <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-brand-red" />
            <span className="text-brand-red font-black uppercase tracking-[0.3em] text-[10px]">
              Investment Timeline
            </span>
          </div>

          <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tighter leading-tight">
            ยิ่งตัดสินใจเร็ว ประหยัดมากกว่า
          </h2>

          <p className="text-brand-red text-2xl md:text-4xl font-black animate-bounce">
            เหลือเพียง {remainingSeats} ที่นั่งสุดท้าย!!!
          </p>
        </div>

        <div className="space-y-4">
          {tiers.map((tier, index) => {
            const isExpired = index < activeIndex;
            const isActive = index === activeIndex;
            const conditionText =
              index === 4
                ? `หน้างาน / ${formatThaiDate(tier.date)}`
                : `ลงทะเบียนก่อน ${formatThaiDate(tier.date)}`;

            return (
              <motion.div
                key={tier.id}
                className={`relative flex flex-col md:flex-row items-center gap-6 p-6 border-2 transition-all duration-500 ${
                  isActive
                    ? 'bg-white text-zinc-900 border-brand-red shadow-[0_0_50px_rgba(211,47,47,0.2)] scale-[1.02] z-10'
                    : isExpired
                      ? 'bg-zinc-900/30 border-zinc-800 opacity-50'
                      : 'bg-zinc-900/50 border-zinc-800 opacity-40'
                }`}
              >
                <div
                  className={`absolute top-0 right-10 -translate-y-1/2 px-3 py-1 text-[9px] font-black uppercase tracking-widest ${
                    isActive
                      ? 'bg-brand-red text-white'
                      : 'bg-zinc-800 text-zinc-500'
                  }`}
                >
                  {isExpired
                    ? 'Expired'
                    : isActive
                      ? 'Current Price'
                      : 'Upcoming'}
                </div>

                <div
                  className={`w-10 h-10 shrink-0 flex items-center justify-center font-black text-lg ${
                    isActive
                      ? 'bg-brand-red text-white'
                      : 'bg-zinc-800 text-zinc-500'
                  }`}
                >
                  {tier.id}
                </div>

                <div className="flex-grow text-center md:text-left">
                  <h3
                    className={`text-lg font-black uppercase mb-0.5 ${
                      isExpired ? 'text-zinc-500' : ''
                    }`}
                  >
                    {tier.name}
                  </h3>

                  <div className="text-[11px] font-bold opacity-60 flex items-center justify-center md:justify-start gap-2">
                    <SafeIcon icon={FiClock} className="w-3 h-3" />
                    {conditionText}
                  </div>

                  {isActive && tier.date && (
                    <TierCountdown targetDate={tier.date} now={now} />
                  )}
                </div>

                <div className="flex flex-col items-center md:items-end min-w-[160px]">
                  <div
                    className={`text-4xl font-black tracking-tighter relative ${
                      isExpired
                        ? 'text-zinc-600'
                        : isActive
                          ? 'text-brand-red'
                          : 'text-zinc-400'
                    }`}
                  >
                    <span className="text-xl mr-1">฿</span>
                    {tier.price || '-'}
                    {isExpired && (
                      <div className="absolute inset-x-[-10%] top-1/2 h-[3px] bg-brand-red/60 -rotate-3 z-20" />
                    )}
                  </div>
                </div>

                <div className="md:pl-4">
                  <a
                    href="#registration"
                    className={`px-8 py-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-3 transition-all ${
                      isExpired
                        ? 'bg-zinc-800 text-zinc-600 pointer-events-none'
                        : isActive
                          ? 'bg-zinc-900 text-white hover:bg-brand-red shadow-lg'
                          : 'bg-white text-zinc-900'
                    }`}
                  >
                    {isExpired ? 'SOLD OUT' : 'Book Now'}
                    {!isExpired && <SafeIcon icon={FiArrowRight} />}
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}